import {
  Autocomplete,
  CircularProgress,
  Modal,
  TextField,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import React, { useEffect, useRef, useState } from "react";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import CustomButton from "../Buttons/Button";
import { getAllUser } from "../../store/actions/userManagement/management";
import { searchUsers } from "../../store/actions/userManagement/searchUsers";
import { calculateIpoForUser } from "../../store/actions/ipos/calculateIpoForUser";
import { subscribeIpoForUser } from "../../store/actions/ipos/subscribeIpoForUser";
import { clearIpoQuote } from "../../store/reducers/iposSlice";
import { getCurrencySymbol } from "../../Helper/getCurrencySymbol";

const buildUserOption = (user) => ({
  ...user,
  label: `${user?.name || "Unnamed"} — ${user?.email || ""} (ID: ${user?.id})`,
});

const modeOptions = [
  { value: "amount", label: "By amount" },
  { value: "shares", label: "By number of shares" },
];

const QUOTE_DEBOUNCE_MS = 600;

const formatMoney = (currency, value) =>
  value != null
    ? `${getCurrencySymbol(currency)} ${Number(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    : "-";

const Row = ({ label, value, bold }) => (
  <div className="flex justify-between text-sm py-1">
    <span className="text-[#475467]">{label}</span>
    <span className={bold ? "font-[600]" : "font-[500]"}>{value}</span>
  </div>
);

function SubscribeIpoForUserModal({ open, onClose, ipo }) {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.ipos?.subscribeForUserPending);
  const quotePending = useSelector((state) => state?.ipos?.quotePending);
  const quote = useSelector((state) => state?.ipos?.ipoQuote);
  const allUsers = useSelector((state) => state?.user?.allUsers) || [];
  const allUsersPending = useSelector((state) => state?.user?.allUsersPending);

  const [selectedUser, setSelectedUser] = useState(null);
  const [mode, setMode] = useState("amount");
  const [value, setValue] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const searchTimer = useRef(null);
  const quoteTimer = useRef(null);

  const currency = ipo?.currency;
  const currencySymbol = getCurrencySymbol(currency);

  useEffect(() => {
    if (open) {
      setSelectedUser(null);
      setMode("amount");
      setValue("");
      setInputValue("");
      setSearchResults([]);
      dispatch(clearIpoQuote());
      if (!allUsers || allUsers.length < 1) {
        dispatch(getAllUser());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Debounced server-side search so admins can find users not in the
  // initially loaded list.
  const handleInputChange = (_, nextValue, reason) => {
    setInputValue(nextValue);
    if (reason === "reset") return;

    if (searchTimer.current) clearTimeout(searchTimer.current);

    const term = nextValue?.trim();
    if (!term || term.length < 2) {
      setSearching(false);
      setSearchResults([]);
      return;
    }

    setSearching(true);
    searchTimer.current = setTimeout(() => {
      dispatch(searchUsers(term))
        .unwrap()
        .then((users) => setSearchResults(Array.isArray(users) ? users : []))
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 400);
  };

  const buildBody = () => ({
    user_id: Number(selectedUser?.id),
    mode,
    ...(mode === "amount"
      ? { amount: Number(value) }
      : { shares: Number(value) }),
  });

  // Re-quote (debounced) whenever the user, mode or figure changes. The
  // quote endpoint writes nothing and answers 200 even for invalid input,
  // carrying summary.is_valid / validation_message.
  useEffect(() => {
    if (quoteTimer.current) clearTimeout(quoteTimer.current);
    if (!open) return;

    if (!selectedUser?.id || !value || Number(value) <= 0) {
      dispatch(clearIpoQuote());
      return;
    }

    quoteTimer.current = setTimeout(() => {
      dispatch(calculateIpoForUser({ id: ipo?.id, data: buildBody() }));
    }, QUOTE_DEBOUNCE_MS);

    return () => {
      if (quoteTimer.current) clearTimeout(quoteTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedUser?.id, mode, value, ipo?.id]);

  useEffect(() => {
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
      if (quoteTimer.current) clearTimeout(quoteTimer.current);
    };
  }, []);

  const hasSearchTerm = inputValue?.trim()?.length >= 2;
  const baseUsers = hasSearchTerm ? searchResults : allUsers || [];
  const mergedUsers = [
    ...(selectedUser ? [selectedUser] : []),
    ...baseUsers,
  ].reduce((acc, user) => {
    if (user?.id != null && !acc.some((u) => u?.id === user.id)) {
      acc.push(user);
    }
    return acc;
  }, []);

  const userOptions = mergedUsers.map(buildUserOption);

  const summary = quote?.summary;
  // The quote belongs to the current inputs only if it was built from them.
  const quoteMatches =
    summary &&
    quote?.user?.id === selectedUser?.id &&
    summary?.entry_mode === mode &&
    (mode === "amount"
      ? Number(summary?.requested_amount) === Number(value)
      : Number(summary?.shares) === Number(value));
  const canSubmit = quoteMatches && summary?.is_valid && !quotePending;

  const handleSubmit = () => {
    if (!selectedUser?.id) {
      toast.error("Please select a user to subscribe for.");
      return;
    }
    if (!value || Number(value) <= 0) {
      toast.error(
        mode === "amount"
          ? "Please enter a valid amount."
          : "Please enter a valid number of shares."
      );
      return;
    }
    dispatch(
      subscribeIpoForUser({
        id: ipo?.id,
        data: buildBody(),
        callback: () => onClose(),
      })
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 520,
          maxWidth: "92%",
          maxHeight: "90%",
          overflowY: "auto",
          bgcolor: "background.paper",
          borderRadius: "10px",
          boxShadow: 24,
          p: 2,
        }}
      >
        <IconButton
          aria-label="close"
          sx={{ position: "absolute", top: 19, right: 10 }}
          onClick={onClose}
        >
          <ClearIcon />
        </IconButton>

        <div className="w-full p-3">
          <h1 className="font-semibold text-2xl mb-1">Subscribe for a User</h1>
          <p className="text-sm mb-6 text-[#475467]">
            Subscribe to the {ipo?.stock?.symbol ? `${ipo.stock.symbol} ` : ""}
            offer on behalf of a user at {formatMoney(currency, ipo?.offer_price)}{" "}
            per share. Their wallet is debited immediately.
          </p>

          <label className="text-sm font-[600] text-[#344054]">
            Select User
          </label>
          <div className="w-full mb-4 mt-1">
            <Stack spacing={2} sx={{ width: "100%" }}>
              <Autocomplete
                options={userOptions}
                value={selectedUser}
                inputValue={inputValue}
                loading={allUsersPending || searching}
                filterOptions={(options) => options}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, v) => option?.id === v?.id}
                onChange={(_, newValue) => setSelectedUser(newValue)}
                onInputChange={handleInputChange}
                noOptionsText={
                  inputValue?.trim()?.length >= 2
                    ? "No users found"
                    : "Type at least 2 characters to search"
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search user by name, email or ID"
                    sx={{
                      "& .MuiInputBase-input": {
                        height: "20px",
                        padding: "15px",
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {searching ? (
                            <CircularProgress color="inherit" size={18} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Stack>
          </div>

          <label className="text-sm font-[600] text-[#344054]">
            Subscribe
          </label>
          <select
            value={mode}
            onChange={(e) => {
              setMode(e.target.value);
              setValue("");
            }}
            className="w-full border border-[#D0D5DD] rounded-lg p-3 mt-1 mb-4 outline-none"
          >
            {modeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <label className="text-sm font-[600] text-[#344054]">
            {mode === "amount" ? `Amount (${currencySymbol})` : "Number of Shares"}
          </label>
          <input
            type="number"
            min={0}
            step={mode === "shares" ? 1 : "any"}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={
              mode === "amount"
                ? `Minimum ${formatMoney(currency, ipo?.min_investment_amount)}`
                : `Minimum ${ipo?.min_subscription_shares ?? "-"} shares`
            }
            className="w-full border border-[#D0D5DD] rounded-lg p-3 mt-1 mb-4 outline-none"
          />

          {(quotePending || quoteMatches) && (
            <div className="border border-[#EAECF0] rounded-lg p-4 mb-4">
              <div className="flex justify-between items-center mb-2">
                <h2 className="font-[600] text-sm">Review</h2>
                {quotePending && <CircularProgress size={16} />}
              </div>

              {quoteMatches && (
                <>
                  <Row label="Share Price" value={formatMoney(currency, summary.offer_price)} />
                  <Row label="Shares Requested" value={Number(summary.shares ?? 0).toLocaleString()} />
                  <Row label="Subscription Amount" value={formatMoney(currency, summary.subscription_amount)} />
                  <Row
                    label={`Admin Fee (${summary.admin_fee_pct ?? ipo?.admin_fee_pct ?? "-"}%)`}
                    value={formatMoney(currency, summary.admin_fee)}
                  />
                  <Row label="Total Debit" value={formatMoney(currency, summary.total_debit)} bold />
                  <hr className="border-[#EAECF0] my-2" />
                  <Row label="Current Wallet Balance" value={formatMoney(currency, summary.wallet_balance)} />
                  <Row
                    label="Balance After Subscription"
                    value={formatMoney(currency, summary.balance_after_subscription)}
                    bold
                  />

                  {summary.rounding_note && (
                    <p className="text-xs text-[#475467] bg-[#F9FAFB] border border-[#EAECF0] rounded-lg p-2 mt-3">
                      {summary.rounding_note}
                    </p>
                  )}

                  {!summary.is_valid && summary.validation_message && (
                    <p className="text-xs text-[#B42318] bg-[#FEF3F2] border border-[#FDA29B] rounded-lg p-2 mt-3">
                      {summary.validation_message}
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          <div className="flex mt-2 w-full items-center">
            <CustomButton
              variant="outlined"
              customColor="#fff"
              children="Cancel"
              width="100%"
              height="44px"
              borderColor="#D0D5DD"
              color="#344054"
              margin="0px 25px 0px 0px"
              onClick={onClose}
            />
            <CustomButton
              variant="contained"
              customColor="#159AA8"
              children="Subscribe"
              width="100%"
              height="44px"
              loading={loading}
              padding="10px"
              color="#fff"
              disabled={!canSubmit || loading}
              onClick={handleSubmit}
            />
          </div>
        </div>
      </Box>
    </Modal>
  );
}

export default SubscribeIpoForUserModal;
